<template>
  <div class="sms-step-form">
    <div class="sms-step-form__row">
      <div class="sms-step-form__field">
        <label>Порядок</label>
        <DxNumberBox
          :value="modelValue.step_order"
          :min="1"
          :show-spin-buttons="true"
          @value-changed="patch('step_order', Number($event.value) || 1)"
        />
      </div>
      <div class="sms-step-form__field">
        <label>Timeout, сек</label>
        <DxNumberBox
          :value="modelValue.timeout"
          :min="0"
          :show-spin-buttons="true"
          @value-changed="patch('timeout', Number($event.value) || 0)"
        />
      </div>
      <div class="sms-step-form__field">
        <label>Повторы при ошибке</label>
        <DxNumberBox
          :value="modelValue.retries"
          :min="0"
          :show-spin-buttons="true"
          @value-changed="patch('retries', Number($event.value) || 0)"
        />
      </div>
    </div>

    <div class="sms-step-form__field">
      <label>Действие</label>
      <DxTextBox :value="taskLabel" :read-only="true" />
    </div>

    <div class="sms-step-form__field">
      <label>Имя отправителя</label>
      <DxSelectBox
        :value="modelValue.sender_name"
        :items="senderItems"
        value-expr="value"
        display-expr="text"
        :search-enabled="true"
        :accept-custom-value="true"
        :show-clear-button="true"
        placeholder="Sender name"
        @customItemCreating="onCustomSender"
        @value-changed="onSenderChanged"
      />
    </div>

    <div class="sms-step-form__field">
      <label>Текст сообщения</label>
      <DxTextArea
        :value="modelValue.message_text"
        :height="140"
        :max-length="0"
        @value-changed="patch('message_text', String($event.value ?? ''))"
      />
    </div>

    <div class="sms-step-form__field">
      <label>Поле номера телефона</label>
      <DxTextBox
        :value="modelValue.phone_column"
        placeholder="phone"
        @value-changed="patch('phone_column', String($event.value ?? '') || 'phone')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxTextArea } from 'devextreme-vue/text-area';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { getSenderNamesLookup } from '@botto/core';
import { type SendSmsStepFormState } from './sendSmsSettings';

const props = defineProps<{
  modelValue: SendSmsStepFormState;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: SendSmsStepFormState): void;
}>();

const senderNamesLookup = getSenderNamesLookup();
const taskLabel = 'Отправить SMS';

function senderLabel(name: string | null | undefined, value: string | null | undefined): string {
  return (name || value || '').trim();
}

const senderItems = computed(() => {
  const seen = new Set<string>();
  const items: { value: string; text: string }[] = [];
  for (const row of senderNamesLookup.items.value) {
    const value = senderLabel(row.name, row.value);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    items.push({ value, text: value });
  }
  const current = props.modelValue.sender_name.trim();
  if (current && !seen.has(current)) {
    items.unshift({ value: current, text: current });
  }
  return items;
});

onMounted(() => {
  void senderNamesLookup.ensureLoaded();
});

function patch<K extends keyof SendSmsStepFormState>(key: K, value: SendSmsStepFormState[K]) {
  emit('update:modelValue', { ...props.modelValue, [key]: value });
}

function onSenderChanged(e: { value?: string | null; event?: Event }) {
  if (!e.event && e.value === props.modelValue.sender_name) return;
  patch('sender_name', e.value ?? '');
}

function onCustomSender(e: { text?: string; customItem?: unknown }) {
  const value = (e.text ?? '').trim();
  e.customItem = value ? { value, text: value } : null;
}
</script>

<style scoped>
.sms-step-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sms-step-form__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.sms-step-form__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sms-step-form__field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

@media (max-width: 640px) {
  .sms-step-form__row {
    grid-template-columns: 1fr;
  }
}
</style>
